import React, { useState, useEffect, useRef } from "react";
import PricingDialog from "../dialogs/PricingDialog"; // Adjust path if needed
import SubscriptionDialog from "../dialogs/SubscriptionDialog";
import WaveSurfer from "wavesurfer.js";
import { HiMiniPlay, HiMiniPause } from "react-icons/hi2";
import { FaMusic } from "react-icons/fa";
import { MdVideoLibrary, MdVolumeUp, MdAccessTime } from "react-icons/md";
import { useAuth } from "../context/AuthContext";
import { MdCheck } from "react-icons/md";

export default function MainServicePage() {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [duration, setDuration] = useState(20);
  const [holdDrawn, setHoldDrawn] = useState(3);
  const [holdFinal, setHoldFinal] = useState(2);
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [audioFile, setAudioFile] = useState(null);
  const [audioStartTime, setAudioStartTime] = useState(0);
  const [audioFadeout, setAudioFadeout] = useState("2");
  const [fps, setFps] = useState(30);
  const [maxResolution, setMaxResolution] = useState("720p (1280x720)");
  const [outroText, setOutroText] = useState(
    "Thank you for watching!\nSubscribe for more!"
  );
  const [enableTts, setEnableTts] = useState(false);
  const [ttsEngine, setTtsEngine] = useState("gtts");
  const [voiceSpeed, setVoiceSpeed] = useState(1);
  const [outroDuration, setOutroDuration] = useState(5);
  const [outroLogo, setOutroLogo] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioStartTimeInput, setAudioStartTimeInput] = useState("0");

  const [currentTime, setCurrentTime] = useState("0.00");
  const [totalDuration, setTotalDuration] = useState("0.00");
  const [progress, setProgress] = useState(0);

  const waveSurferRef = useRef(null);
  const wavesurferInstance = useRef(null);

  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);

  const { user, isSubscribed } = useAuth();

  const [isGenerating, setIsGenerating] = useState(false);

  const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
  const [isDownloadEnabled, setIsDownloadEnabled] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [audioDragActive, setAudioDragActive] = useState(false);
  const [outroLogoDragActive, setOutroLogoDragActive] = useState(false);

  const [recommendedRatio, setRecommendedRatio] = useState(null);
  const [currentRatio, setCurrentRatio] = useState(null);
  const [logoRecommendedRatio, setLogoRecommendedRatio] = useState(null);
  const [logoCurrentRatio, setLogoCurrentRatio] = useState(null);

  useEffect(() => {
    setAspectRatio(recommendedRatio);
  }, [recommendedRatio]);

  useEffect(() => {
    setAudioStartTimeInput(audioStartTime.toFixed(2));
  }, [audioStartTime]);

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setUploadedFile(file);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/processing/analyze-image`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error("Failed to analyze image");
        }

        const data = await response.json();
        setRecommendedRatio(data.recommended_ratio);
        setCurrentRatio(data.current_ratio);
      } catch (error) {
        console.error("Error analyzing image:", error);
        setRecommendedRatio(null);
        setCurrentRatio(null);
      }
    }
  };

  const handleAudioChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setAudioFile(e.target.files[0]);
      setAudioStartTime(0);
      setAudioPlaying(false);
    }
  };

  const handleFadeoutChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^[0-9]*\.?[0-9]*$/.test(val)) {
      setAudioFadeout(val);
    }
  };

  const handleAudioStartTimeInputChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^[0-9]*\.?[0-9]*$/.test(val)) {
      setAudioStartTimeInput(val);
    }
  };

  const handleAudioStartTimeInputBlur = () => {
    const newValue = parseFloat(audioStartTimeInput) || 0;
    setAudioStartTime(newValue);
  };

  const formatTime = (seconds) => {
    return seconds.toFixed(2);
  };

  useEffect(() => {
    if (audioFile && waveSurferRef.current) {
      if (wavesurferInstance.current) {
        wavesurferInstance.current.destroy();
      }
      const ws = WaveSurfer.create({
        container: waveSurferRef.current,
        waveColor: "#ff0000",
        progressColor: "#2575fc",
        cursorColor: "#a0a0f0",
        barWidth: 2,
        height: 80,
        responsive: true,
        normalize: true,
      });
      wavesurferInstance.current = ws;

      const objectUrl = URL.createObjectURL(audioFile);
      ws.load(objectUrl);

      ws.on("ready", () => {
        ws.seekTo(0);
        const duration = ws.getDuration();
        setTotalDuration(formatTime(duration));
        setCurrentTime(formatTime(0));
        setProgress(0); // <-- ADDED THIS LINE
      });

      ws.on("audioprocess", (time) => {
        setCurrentTime(formatTime(time));
        // ADDED LOGIC to calculate and set progress
        const duration = wavesurferInstance.current.getDuration();
        if (duration > 0) {
          setProgress(time / duration);
        }
      });

      ws.on("seek", (progressValue) => {
        // Renamed 'progress' to 'progressValue'
        setProgress(progressValue); // <-- ADDED THIS LINE
        const duration = ws.getDuration();
        if (!isNaN(duration)) {
          const newTime = duration * progressValue;
          setAudioStartTime(newTime);
          setCurrentTime(formatTime(newTime));

          ws.setWaveColor([
            [0, progressValue * 100, "#ff0000"],
            [progressValue * 100, 100, "#2575fc"],
          ]);
        }
      });

      ws.on("finish", () => {
        setAudioPlaying(false);
        setCurrentTime(formatTime(0));
        setProgress(0); // <-- ADDED THIS LINE
        ws.seekTo(0);
      });

      return () => {
        ws.destroy();
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [audioFile]);

  const togglePlay = () => {
    if (!wavesurferInstance.current) return;
    if (audioPlaying) {
      wavesurferInstance.current.pause();
      setAudioPlaying(false);
    } else {
      const dur = wavesurferInstance.current.getDuration();
      const seekPos = audioStartTime / dur;
      wavesurferInstance.current.seekTo(seekPos);
      wavesurferInstance.current.play();
      setAudioPlaying(true);
    }
  };

  const setCurrentStartTime = () => {
    if (!wavesurferInstance.current) return;
    const currentTime = wavesurferInstance.current.getCurrentTime();
    setAudioStartTime(parseFloat(currentTime.toFixed(2)));
  };

  const handleOutroLogoChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setOutroLogo(file);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/processing/analyze-image`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error("Failed to analyze logo image");
        }

        const data = await response.json();
        setLogoRecommendedRatio(data.recommended_ratio);
        setLogoCurrentRatio(data.current_ratio);
      } catch (error) {
        console.error("Error analyzing logo image:", error);
        setLogoRecommendedRatio(null);
        setLogoCurrentRatio(null);
      }
    }
  };

  const handleGenerate = async () => {
    if (!uploadedFile) {
      alert("Please upload an image first!");
      return;
    }
    if (!user) {
      alert("You must be signed in to generate a video.");
      return;
    }

    try {
      setIsGenerating(true);
      const formData = new FormData();
      formData.append("image", uploadedFile);
      formData.append(
        "settings_json",
        JSON.stringify({
          resolution: maxResolution.split(" ")[0],
          duration,
          hold_drawn: holdDrawn,
          hold_final: holdFinal,
          aspect_ratio: aspectRatio,
          fps,
          outro_text: outroText,
          outro_duration: outroDuration,
          audio_start_time: audioStartTime,
          audio_fadeout: audioFadeout,
        })
      );
      formData.append("email", user?.email || "");
      formData.append("user_id", user?.uid || "");
      if (outroLogo) formData.append("logo", outroLogo);
      if (audioFile) formData.append("audio", audioFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/video/generate`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.status === 429) {
        alert(
          "You have reached the daily limit for video generation. Please try again tomorrow."
        );
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to generate video");
      }

      const data = await response.json();

      const isActiveSubscriber = data["X-Is-Active-Subscriber"] === "true";
      setIsDownloadEnabled(isActiveSubscriber);

      const apiUrl = new URL(process.env.REACT_APP_API_URL);
      const baseUrl = apiUrl.origin;
      const absoluteVideoUrl = `${baseUrl}${data.video_url}`;
      setGeneratedVideoUrl(absoluteVideoUrl);
    } catch (error) {
      alert(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadClick = async () => {
    if (!isDownloadEnabled) {
      setShowSubscriptionDialog(true);
      return;
    }
    if (!generatedVideoUrl) return;

    setIsDownloading(true);

    try {
      // 1. Fetch the video from the URL
      const response = await fetch(generatedVideoUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // 2. Get the video data as a Blob
      const blob = await response.blob();

      // 3. Create a temporary local URL for the blob
      const objectUrl = URL.createObjectURL(blob);

      // 4. Create a hidden link and click it to trigger download
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "neon_drawing.mp4"; // Your desired filename
      document.body.appendChild(link);
      link.click();

      // 5. Clean up by removing the link and revoking the object URL
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert("An error occurred while downloading the file.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#f6f5fc] py-12 px-2 font-sans">
        {/* HEADER AREA */}
        <div className="mb-12 flex flex-col items-center">
          <h1 className="text-[2.1rem] md:text-4xl font-bold text-gray-900 mb-4 mt-4 text-center leading-snug drop-shadow-sm flex items-center gap-3">
            <MdVideoLibrary
              className="text-[#6a11cb] hidden md:block"
              size={36}
            />
            Turn your Chat GPT neon art into live video drawings!
          </h1>
          <div className="text-lg text-gray-700 text-center max-w-2xl flex items-center gap-2">
            <MdVolumeUp className="text-[#2575fc] hidden md:block" size={24} />
            Turn your AI generated art or images into mesmerizing live-drawing
            videos with natural edges, glowing colors, and smooth transitions—no
            editing skills needed.
          </div>
        </div>

        {/* MAIN FORM CONTENT */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Upload Section */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 flex flex-col">
            <h2 className="text-[1.29rem] font-bold mb-3 text-gray-900 tracking-tight">
              1. Upload Your Image
            </h2>
            <div className="text-gray-500 mb-7 text-[1.06rem] font-normal">
              Supported formats: <span className="font-semibold">PNG, JPG</span>
              . Choose your image to start creating your unique neon drawing
              animation.
            </div>
            <div className="relative group inline-block w-full">
              <div
                className={`relative group w-full border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${
                  !user
                    ? "border-gray-300 bg-gray-100 cursor-not-allowed opacity-50"
                    : "border-blue-400 bg-white hover:bg-blue-50"
                }`}
                onClick={() => {
                  if (!user) return;
                  document.getElementById("file-upload-input").click();
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!user) return;
                  e.currentTarget.classList.add(
                    "bg-blue-100",
                    "border-blue-600"
                  );
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  if (!user) return;
                  e.currentTarget.classList.remove(
                    "bg-blue-100",
                    "border-blue-600"
                  );
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!user) return;
                  e.currentTarget.classList.remove(
                    "bg-blue-100",
                    "border-blue-600"
                  );

                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    const syntheticEvent = {
                      target: {
                        files: e.dataTransfer.files,
                      },
                    };

                    handleFileChange(syntheticEvent);

                    e.dataTransfer.clearData();
                  }
                }}
              >
                <input
                  id="file-upload-input"
                  type="file"
                  accept="image/png, image/jpeg"
                  disabled={!user}
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="flex flex-col items-center justify-center gap-3 select-none px-2">
                  <svg
                    className="h-10 w-10 text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 15a4 4 0 004 4h10a4 4 0 004-4v-3a4 4 0 00-4-4H7a4 4 0 00-4 4v3z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 10l4-4m0 0l4 4m-4-4v12"
                    />
                  </svg>
                  <p className="text-gray-700 font-medium">
                    Drag and drop your image here, or click to upload
                  </p>
                </div>
              </div>
              {uploadedFile && (
                <div className="ratio-info mt-2 text-left text-gray-700">
                  <p>
                    The approximate ratio of this image is:{" "}
                    {recommendedRatio !== null
                      ? recommendedRatio
                      : "Loading..."}
                  </p>
                  {/* <p>
                    Current Ratio:{" "}
                    {currentRatio !== null ? currentRatio : "Loading..."}
                  </p> */}
                </div>
              )}

              <label className="block font-semibold text-gray-700 mb-1 mt-4">
                Supported ratios are 9:16, 16:9, 4:5, 1:1. When you upload an
                image the supported ratio will be automatically selected. You
                can change it further.
              </label>
              <div className="flex gap-4 flex-wrap">
                {["16:9", "9:16", "4:5", "1:1"].map((ratio) => (
                  <button
                    key={ratio}
                    type="button"
                    // onClick={() => setAspectRatio(ratio)}
                    className={`px-6 py-2 rounded font-medium transition-all flex items-center gap-3 ${
                      aspectRatio === ratio
                        ? "bg-blue-600 text-white shadow-md"
                        : "bg-[#faf9fd] text-gray-900 border border-gray-300 hover:bg-gray-200"
                    }`}
                  >
                    {ratio}
                    {aspectRatio === ratio && (
                      <MdCheck className="text-green-400 text-xl" />
                    )}
                  </button>
                ))}
              </div>

              {/* Tooltip */}
              {!user && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-3 py-1 rounded bg-gray-900 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
                  You must sign in to Google to use this app
                </div>
              )}
            </div>

            <div className="text-sm h-6">
              {uploadedFile && (
                <span className="text-lime-600 font-medium transition-colors">
                  Uploaded: {uploadedFile.name}
                </span>
              )}
            </div>
          </div>

          {/* Basic Settings Section */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 flex flex-col">
            <h2 className="text-[1.29rem] font-bold mb-3 text-gray-900 tracking-tight">
              2. Basic Animation Settings
            </h2>
            <div className="flex flex-col gap-6">
              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  Animation Duration
                </label>
                <input
                  type="range"
                  min="5"
                  max="60"
                  value={duration}
                  onChange={(e) => setDuration(+e.target.value)}
                  className="w-full"
                />
                <div className="text-gray-500 text-sm mt-1 mb-2">
                  {duration} seconds · How long the live drawing playback lasts.
                </div>
              </div>
              <div className="flex gap-6">
                <div className="flex-1">
                  <label className="block font-semibold text-gray-700 mb-1">
                    Hold Drawn Image
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={holdDrawn}
                    onChange={(e) => setHoldDrawn(+e.target.value)}
                    className="w-full"
                  />
                  <div className="text-gray-500 text-sm mt-1">
                    {holdDrawn} seconds
                  </div>
                </div>
                <div className="flex-1">
                  <label className="block font-semibold text-gray-700 mb-1">
                    Hold Final Image
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={holdFinal}
                    onChange={(e) => setHoldFinal(+e.target.value)}
                    className="w-full"
                  />
                  <div className="text-gray-500 text-sm mt-1">
                    {holdFinal} seconds
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Video Settings Section */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 flex flex-col md:col-span-2">
            <h2 className="text-[1.29rem] font-bold mb-3 text-gray-900 tracking-tight">
              3. Video & Audio Settings
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Video formats */}
              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  Frame Rate (FPS)
                </label>
                <input
                  type="range"
                  min="24"
                  max="60"
                  value={fps}
                  onChange={(e) => setFps(+e.target.value)}
                  className="w-full mb-1"
                />
                <div className="text-gray-500 text-sm mb-4">
                  {fps} frames per second
                </div>

                <label className="block font-semibold text-gray-700 mb-1">
                  Output Resolution
                </label>
                <select
                  value={maxResolution}
                  onChange={(e) => setMaxResolution(e.target.value)}
                  className="w-full mb-2 p-2 rounded bg-[#faf9fd] text-gray-900 border border-gray-300"
                >
                  <option>720p (1280x720)</option>
                  <option>1080p (1920x1080)</option>
                  <option value="4K (3840x2160)" disabled={!isSubscribed}>
                    4K (3840x2160) {!isSubscribed && "(Subscribe to unlock)"}
                  </option>
                </select>
              </div>

              {/* Audio settings */}
              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  Background Audio (Optional)
                </label>

                <div
                  className={`relative border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${
                    audioDragActive
                      ? "bg-blue-100 border-blue-600"
                      : "border-gray-300 bg-[#faf9fd]"
                  }`}
                  onClick={() =>
                    document.getElementById("audio-upload-input").click()
                  }
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setAudioDragActive(true);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setAudioDragActive(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setAudioDragActive(false);
                    if (
                      e.dataTransfer.files &&
                      e.dataTransfer.files.length > 0
                    ) {
                      handleAudioChange({
                        target: { files: e.dataTransfer.files },
                      });
                      e.dataTransfer.clearData();
                    }
                  }}
                >
                  <input
                    id="audio-upload-input"
                    type="file"
                    accept="audio/mp3,audio/wav,audio/ogg,audio/m4a"
                    onChange={handleAudioChange}
                    className="hidden"
                  />
                  <div className="flex flex-col items-center justify-center gap-3 select-none">
                    <FaMusic className="text-blue-500" size={30} />
                    <p className="text-gray-700 font-medium">
                      Drag and drop your audio here, or click to upload
                    </p>
                  </div>
                </div>

                {audioFile && (
                  <div className="mb-2 text-sm text-lime-600 font-medium">
                    Uploaded: {audioFile.name}
                  </div>
                )}

                {/* The rest of your wave surfer UI and controls remain unchanged */}
                {audioFile && (
                  <>
                    <div className="relative">
                      <div
                        ref={waveSurferRef}
                        className="w-full rounded border border-gray-300 shadow-inner mb-2"
                        style={{ minHeight: "80px" }}
                      ></div>

                      {/* New Timer Display Element */}
                      <div
                        className="absolute -bottom-3 -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-70 text-white px-3 py-1 rounded-md text-sm font-mono pointer-events-none z-10"
                        style={{
                          left: `${progress * 100}%`,
                        }}
                      >
                        {currentTime} | {totalDuration}
                      </div>
                    </div>
                    <div className="flex gap-3 mb-4">
                      <button
                        onClick={togglePlay}
                        className="flex items-center justify-center flex-1 gap-2 text-white bg-blue-600 rounded px-4 py-2 hover:bg-blue-700 transition"
                      >
                        {audioPlaying ? (
                          <HiMiniPause size={20} />
                        ) : (
                          <HiMiniPlay size={20} />
                        )}
                        {audioPlaying ? "Pause" : "Play"}
                      </button>
                      <button
                        onClick={setCurrentStartTime}
                        className="flex items-center justify-center flex-1 gap-2 text-gray-900 bg-gray-300 rounded px-4 py-2 hover:bg-gray-400 transition"
                      >
                        <MdAccessTime size={20} />
                        Set Start Time
                      </button>
                    </div>
                  </>
                )}

                {/* Continue with Audio Start Time and Fadeout inputs */}
                <label className="block font-semibold text-gray-700 mb-1">
                  Audio Start Time (seconds)
                </label>
                <input
                  type="text" // Use "text" to allow partial decimals like "1."
                  inputMode="decimal" // Shows decimal keyboard on mobile
                  value={audioStartTimeInput}
                  onChange={handleAudioStartTimeInputChange}
                  onBlur={handleAudioStartTimeInputBlur}
                  className="w-full mb-4 p-2 rounded bg-[#faf9fd] text-gray-900 border border-gray-300"
                />
                <label className="block font-semibold text-gray-700 mb-1">
                  Fade Out Duration
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  min="0"
                  max="10"
                  step="0.5"
                  value={audioFadeout}
                  onChange={handleFadeoutChange}
                  className="w-full p-2 rounded bg-[#faf9fd] text-gray-900 border border-gray-300"
                />
              </div>
            </div>
          </div>

          {/* Outro Settings Section */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 flex flex-col md:col-span-2">
            <h2 className="text-[1.29rem] font-bold mb-3 text-gray-900 tracking-tight">
              4. Outro & Branding (Optional)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="col-span-2">
                <label className="block font-semibold text-gray-700 mb-1">
                  Outro Text
                </label>
                <textarea
                  value={outroText}
                  onChange={(e) => setOutroText(e.target.value)}
                  rows="5"
                  placeholder="Enter custom outro message for your video"
                  className="w-full mb-5 p-2 rounded bg-[#faf9fd] text-gray-900 border border-gray-300 resize-none"
                ></textarea>
                <label className="block font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <MdVolumeUp size={20} />
                  Enable Text-to-Speech for Outro
                </label>
                <input
                  type="checkbox"
                  checked={enableTts}
                  onChange={() => setEnableTts(!enableTts)}
                  className="mb-6"
                />
                {enableTts && (
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block font-semibold text-gray-700 mb-1">
                        TTS Engine
                      </label>
                      <select
                        value={ttsEngine}
                        onChange={(e) => setTtsEngine(e.target.value)}
                        className="w-full mb-4 p-2 rounded bg-[#faf9fd] text-gray-900 border border-gray-300"
                      >
                        <option value="gtts">gtts</option>
                        <option value="pyttsx3">pyttsx3</option>
                        <option value="edge-tts">edge-tts</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block font-semibold text-gray-700 mb-1">
                        Voice Speed
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={voiceSpeed}
                        onChange={(e) => setVoiceSpeed(+e.target.value)}
                        className="w-full mb-4"
                      />
                      <div className="text-center text-sm text-gray-500">
                        {voiceSpeed.toFixed(1)}x
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  End Slide (Optional)
                </label>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${
                    outroLogoDragActive
                      ? "bg-blue-100 border-blue-600"
                      : "border-gray-300 bg-[#faf9fd]"
                  }`}
                  onClick={() =>
                    document.getElementById("outro-logo-input").click()
                  }
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setOutroLogoDragActive(true);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setOutroLogoDragActive(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setOutroLogoDragActive(false);
                    if (
                      e.dataTransfer.files &&
                      e.dataTransfer.files.length > 0
                    ) {
                      handleOutroLogoChange({
                        target: { files: e.dataTransfer.files },
                      });
                      e.dataTransfer.clearData();
                    }
                  }}
                >
                  <input
                    id="outro-logo-input"
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleOutroLogoChange}
                    className="hidden"
                  />
                  <div className="text-center text-gray-700 select-none">
                    Drag and drop your logo here, or click to upload
                  </div>
                </div>

                {outroLogo && (
                  <div className="ratio-info mt-2 text-left text-gray-700">
                    <p>
                      The recommended ratio for the End Slide image is:{" "}
                      {logoRecommendedRatio !== null
                        ? logoRecommendedRatio
                        : "Loading..."}
                    </p>
                    {/* <p>
                      Logo Current Ratio:{" "}
                      {logoCurrentRatio !== null
                        ? logoCurrentRatio
                        : "Loading..."}
                    </p> */}
                    <p className="mb-3 text-sm text-lime-600 font-medium">
                      Uploaded: {outroLogo.name}
                    </p>
                  </div>
                )}
                <label className="block font-semibold text-gray-700 mb-1">
                  Outro Duration
                </label>
                <input
                  type="range"
                  min="2"
                  max="15"
                  value={outroDuration}
                  onChange={(e) => setOutroDuration(+e.target.value)}
                  className="w-full mb-3"
                />
                <div className="text-center text-sm text-gray-500">
                  {outroDuration} seconds
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Generate Video button */}
        <div className="max-w-6xl mx-auto mt-12 px-6 relative group">
          <button
            onClick={handleGenerate}
            className={`w-full max-w-xs mx-auto flex items-center justify-center gap-2 text-white text-lg font-bold py-3 px-10 rounded-full shadow-sm transition bg-gradient-to-r from-[#6a11cb] to-[#2575fc] ${
              !user || isGenerating || currentRatio === null
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-blue-700"
            }`}
            disabled={!user || isGenerating || currentRatio === null}
          >
            {isGenerating ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0V4a8 8 0 014.9 14.9l-1.4-1.4a6 6 0 01-8.5-8.5l-1.4-1.4z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <MdVideoLibrary size={24} />
                Generate Video
              </>
            )}
          </button>

          {!user && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-3 py-1 rounded bg-gray-900 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
              You must sign in to Google to use this app
            </div>
          )}
        </div>

        {/* Video preview and download */}
        {generatedVideoUrl && (
          <div className="max-w-6xl mx-auto mt-6 px-6 flex flex-col items-center gap-4">
            <video
              src={generatedVideoUrl}
              controls
              style={{
                width: "420px",
                aspectRatio: aspectRatio.replace(":", "/"),
              }}
              onError={(e) => console.error("Video playback error:", e)}
            />
            <button
              onClick={handleDownloadClick}
              className={`px-6 py-3 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition ${
                !isDownloadEnabled || isDownloading
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              disabled={isDownloading}
            >
              {isDownloading ? "Downloading..." : "Download Video"}
            </button>
          </div>
        )}

        {/* Pricing Dialog */}
        {showPricingDialog && (
          <PricingDialog
            onClose={() => setShowPricingDialog(false)}
            onSubscribe={() => {
              setShowPricingDialog(false);
              setShowSubscriptionDialog(true);
            }}
            onGetStarted={() => {
              setShowPricingDialog(false);
              alert("Show all data here (same as subscriber)");
            }}
          />
        )}

        {/* Subscription Dialog */}
        {showSubscriptionDialog && (
          <SubscriptionDialog
            onClose={() => setShowSubscriptionDialog(false)}
          />
        )}
      </div>
    </>
  );
}
